import { useState } from 'react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { Check, ChevronsUpDown, Plus } from 'lucide-react';

// Dropdown that also accepts a typed value not in the list.
export default function CreatableCombobox({ value = '', options = [], optionGroups, onChange, placeholder = 'Select...', noun = 'value', disabled = false, dark = false, testid }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const trimmed = query.trim();
  const isNew = trimmed && !options.some((o) => o.toLowerCase() === trimmed.toLowerCase());

  const pick = (v) => { onChange(v); setOpen(false); setQuery(''); };

  return (
    <Popover modal open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(''); }}>
      <PopoverTrigger asChild>
        <Button id={testid} type="button" variant="outline" role="combobox" disabled={disabled}
          className={`w-full min-w-0 max-w-full justify-between font-normal h-9 px-3 ${value ? '' : 'text-slate-400'}`} data-testid={testid}>
          <span className="min-w-0 truncate" title={value}>{value || placeholder}</span>
          <ChevronsUpDown size={14} className="opacity-50 shrink-0 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={`w-[--radix-popover-trigger-width] max-w-[90vw] p-0 z-[130] ${dark ? 'dark' : ''}`} align="start" sideOffset={4} data-testid={testid ? `${testid}-options` : undefined}>
        <Command>
          <CommandInput placeholder={`Search or type a ${noun}...`} value={query} onValueChange={setQuery} data-testid={testid ? `${testid}-search` : undefined} />
          <CommandList>
            <CommandEmpty data-testid={testid ? `${testid}-empty` : undefined}>{trimmed ? 'Press Enter or click below to use what you typed.' : `No ${noun}s yet — type to add one.`}</CommandEmpty>
            {isNew && (
              <CommandGroup>
                <CommandItem value={`__new__${trimmed}`} onSelect={() => pick(trimmed)} className="text-sky-700 dark:text-sky-400" data-testid={testid ? `${testid}-use-custom` : undefined}>
                  <Plus size={14} className="mr-2 shrink-0" /><span className="min-w-0 break-all">Use &quot;{trimmed}&quot;</span>
                </CommandItem>
              </CommandGroup>
            )}
            <CommandGroup>
              {value && (
                <CommandItem value="__clear__" onSelect={() => pick('')} className="text-slate-500" data-testid={testid ? `${testid}-clear` : undefined}>
                  <Check size={14} className="mr-2 opacity-0" /> Clear
                </CommandItem>
              )}
            </CommandGroup>
            {(optionGroups || [{ options }]).map((group, index) => (
            <CommandGroup key={group.label || index} heading={group.label} data-testid={testid ? `${testid}-group-${index}` : undefined}>
              {group.options.map((opt) => (
                <CommandItem key={opt} value={opt} onSelect={() => pick(opt)} data-testid={testid ? `${testid}-option-${encodeURIComponent(opt)}` : undefined} className="min-w-0 break-all">
                  <Check size={14} className={`mr-2 ${value.toLowerCase() === opt.toLowerCase() ? 'opacity-100' : 'opacity-0'}`} />
                  {opt}
                </CommandItem>
              ))}
            </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
